import React from 'react';

/** Shared shell for dashboard pages (matches Projects max width + rhythm). */
export const dashboardSectionShell = 'max-w-7xl mx-auto space-y-8 pb-10';

export const dashboardCardClass =
  'rounded-2xl border border-slate-200/90 bg-white shadow-sm overflow-hidden dark:border-slate-700 dark:bg-slate-900';

/** Card / section header strip — dark-safe (no white gradient in dark mode). */
export const dashboardCardHeaderClass =
  'border-b border-slate-100 bg-gradient-to-r from-slate-50/80 to-white px-6 py-4 dark:border-slate-700 dark:from-slate-800 dark:to-slate-900';

/** Modal / workspace header strip — dark-safe. */
export const dashboardModalHeaderSurfaceClass =
  'relative shrink-0 overflow-hidden border-b border-slate-200/80 bg-gradient-to-br from-white via-slate-50/90 to-cyan-50/25 dark:border-slate-700 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800';

/** Invoice builder (create/edit quote) modal shells */
export const invoiceBuilderModalShell =
  'relative flex h-[min(96vh,calc(100dvh-0.5rem))] w-full max-w-[min(96vw,1680px)] flex-col overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-2xl shadow-slate-900/20 dark:border-slate-700 dark:bg-slate-900';

export const invoiceBuilderLeftPanel =
  'flex min-h-0 w-full min-w-0 flex-col border-slate-200 p-5 sm:p-6 lg:w-[62%] lg:border-r lg:bg-slate-50/30 dark:border-slate-700 dark:bg-slate-900';

export const invoiceBuilderRightPanel =
  'flex min-h-0 w-full flex-col border-t border-slate-200 bg-slate-50/80 lg:w-[38%] lg:border-t-0 lg:border-l dark:border-slate-700 dark:bg-slate-950/90';

export const dashboardInputClass =
  'w-full rounded-2xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm shadow-sm placeholder:text-slate-400 focus:ring-2 focus:ring-primary/25 focus:border-primary outline-none transition-shadow dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:placeholder:text-slate-500';

export const dashboardSelectClass =
  'rounded-2xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-700 shadow-sm focus:ring-2 focus:ring-primary/25 focus:border-primary outline-none transition-shadow dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200';

export const dashboardBtnPrimary =
  'inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-white shadow-md shadow-primary/25 hover:bg-primary-dark transition-all';

export const dashboardLabelClass = 'mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300';

export const dashboardTextareaClass =
  'w-full min-h-[5.5rem] resize-y rounded-2xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm shadow-sm placeholder:text-slate-400 focus:ring-2 focus:ring-primary/25 focus:border-primary outline-none transition-shadow dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:placeholder:text-slate-500';

/** List-row actions (compact) */
export const dashboardBtnRowEdit =
  'inline-flex items-center justify-center gap-1.5 rounded-xl border border-primary/30 bg-primary/10 px-3 py-2 text-xs font-semibold text-primary shadow-sm transition-all hover:border-primary/45 hover:bg-primary/15 dark:bg-primary/20 dark:text-cyan-200';

export const dashboardBtnRowDelete =
  'inline-flex items-center justify-center gap-1.5 rounded-xl border border-red-200 bg-white px-3 py-2 text-xs font-semibold text-red-600 shadow-sm transition-all hover:border-red-300 hover:bg-red-50 dark:border-red-800 dark:bg-slate-900 dark:text-red-400 dark:hover:bg-red-950/40';

export const dashboardBtnRowAccent =
  'inline-flex items-center justify-center gap-1.5 rounded-xl border border-cyan-300/60 bg-cyan-50 px-3 py-2 text-xs font-semibold text-cyan-900 shadow-sm transition-all hover:border-cyan-400 hover:bg-cyan-100 dark:border-cyan-700 dark:bg-cyan-950/40 dark:text-cyan-200';

/** Invoice row — slightly larger */
export const dashboardBtnInvoiceEdit =
  'inline-flex items-center justify-center gap-1.5 rounded-xl border border-primary/25 bg-primary px-4 py-2.5 text-sm font-semibold text-white shadow-md shadow-primary/20 transition-all hover:border-primary-dark hover:bg-primary-dark';

export const dashboardBtnInvoiceActions =
  'inline-flex items-center justify-center gap-1.5 rounded-xl border border-accent/35 bg-accent px-4 py-2.5 text-sm font-semibold text-white shadow-md shadow-cyan-500/15 transition-all hover:border-accent-dark hover:bg-accent-dark';

export const dashboardBtnInvoiceDelete =
  'inline-flex items-center justify-center gap-1.5 rounded-xl border border-red-200 bg-red-50 px-4 py-2.5 text-sm font-semibold text-red-600 shadow-sm transition-all hover:border-red-300 hover:bg-red-100 dark:border-red-800 dark:bg-red-950/50 dark:text-red-400 dark:hover:bg-red-950/70';

export const dashboardBtnModalCancel =
  'inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition-all hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700';

export const dashboardBtnModalDanger =
  'inline-flex items-center justify-center rounded-xl border border-red-600 bg-red-600 px-4 py-2.5 text-sm font-semibold text-white shadow-md shadow-red-600/20 transition-all hover:border-red-700 hover:bg-red-700';

export const dashboardModalBackdrop = 'fixed inset-0 z-[99999] bg-slate-900/55 backdrop-blur-sm';

export const dashboardModalWrap = 'fixed inset-0 z-[100000] flex items-center justify-center p-4';

export const dashboardModalPanel =
  'flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-2xl shadow-slate-900/15 dark:border-slate-700 dark:bg-slate-900';

export const dashboardModalPanelMd =
  'flex max-h-[90vh] w-full max-w-md flex-col overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-2xl shadow-slate-900/15 dark:border-slate-700 dark:bg-slate-900';

export const dashboardModalHeader =
  'flex shrink-0 items-center justify-between gap-4 border-b border-slate-100 bg-gradient-to-r from-slate-50/90 to-white px-6 py-4 dark:border-slate-700 dark:from-slate-800 dark:to-slate-900';

export const dashboardModalBody = 'flex-1 overflow-y-auto px-6 py-5';

export const dashboardModalFooter =
  'flex shrink-0 justify-end gap-3 border-t border-slate-100 bg-slate-50/60 px-6 py-4 dark:border-slate-700 dark:bg-slate-800/60';

export const dashboardModalCloseBtn =
  'rounded-xl p-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-200';

/**
 * Hero header aligned with the Projects page (Devello primary + cyan accents).
 */
export function DashboardPageHeader({ eyebrow = 'Dashboard', title, description, action }) {
  return (
    <div className="relative overflow-hidden rounded-2xl border border-slate-200/80 bg-gradient-to-br from-white via-slate-50/80 to-cyan-50/30 px-4 py-4 shadow-sm sm:px-6 sm:py-5 dark:border-slate-700 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800">
      <div className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full bg-cyan-400/10 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-6 -left-8 h-28 w-28 rounded-full bg-primary/5 blur-2xl" />
      <div className="relative flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
        <div className="min-w-0 max-w-2xl">
          {eyebrow ? (
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400 sm:text-xs sm:tracking-[0.2em]">
              {eyebrow}
            </p>
          ) : null}
          <h2 className="mt-0.5 text-xl font-bold tracking-tight text-slate-900 sm:text-2xl dark:text-slate-100">{title}</h2>
          {description ? (
            <p className="mt-1 text-sm leading-snug text-slate-600 sm:mt-1.5 dark:text-slate-400">{description}</p>
          ) : null}
        </div>
        {action ? <div className="shrink-0 sm:self-center">{action}</div> : null}
      </div>
    </div>
  );
}
